import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { TDMTypes } from '@tf/api';
import { IdenfyCheckResult, TFPageContent } from '@tf/shared';
import { Box, Button, Icon, TFCard } from '@tf/ui';
import type React from 'react';
import { useState } from 'react';
import { useApplication, useApplicationMainReview } from '@/components/application/ApplicationProvider';
import { ErrorCard, FormLoadingCard, LivenessCheck, MarkdownText } from '@/components/shared';
import { useApplicationLivenessCheck } from '@/core/api/livenessCheck';
import { useOriginTenantSettings } from '@/core/providers/OriginTenantSettingsProvider';

const LoadingErrorCard: React.FC<{ refetch: () => void }> = ({ refetch }) => {
	const { _ } = useLingui();
	return (
		<ErrorCard
			icon={<Icon.IconExclamationCircle />}
			title={_(msg({ id: 'livenessCheckGuard.loadingError.title', message: 'Failed to load' }))}
			description={_(msg({ id: 'livenessCheckGuard.loadingError.message', message: 'Please try again' }))}
			primaryAction={
				<Button mt="1rem" type="button" onClick={refetch}>
					{_(
						msg({
							id: 'livenessCheckGuard.loadingError.action',
							message: 'Reload',
						}),
					)}
				</Button>
			}
		/>
	);
};

const livenessCheckInfoText = msg({
	id: 'livenessCheckPage.intro',
	message: `
# Liveness check required

To continue with your application, you need to pass a liveness check.`,
});

type ApplicationLivenessCheckSettings =
	TDMTypes.GetClientAreaSettingsByTenantData['settings']['liveness_check_before_application'];

const LivenessCheckScreen: React.FC<React.PropsWithChildren<{ settings: ApplicationLivenessCheckSettings }>> = ({
	settings,
	children,
}) => {
	const { _ } = useLingui();

	const mainReview = useApplicationMainReview();
	const applicationLivenessCheck = useApplicationLivenessCheck(mainReview.graphNodeId);

	const [isFinished, setIsFinished] = useState(false);

	if (applicationLivenessCheck.isPending) {
		return <FormLoadingCard />;
	}

	if (applicationLivenessCheck.isError) {
		return <LoadingErrorCard refetch={applicationLivenessCheck.refetch} />;
	}

	const isNewCheck = applicationLivenessCheck.data.meta.state === 'NEW' && !isFinished;

	const hasAcceptedIdenfyStatus =
		applicationLivenessCheck.data.data.overall === IdenfyCheckResult.APPROVED ||
		applicationLivenessCheck.data.data.overall === IdenfyCheckResult.SUSPECTED ||
		applicationLivenessCheck.data.data.overall === IdenfyCheckResult.REVIEWING;
	const isLockedInStrictMode = settings.mode === 'strict' && !hasAcceptedIdenfyStatus;

	if (isNewCheck || isLockedInStrictMode) {
		return (
			<TFPageContent maw="50rem" m="0">
				<TFCard innerPadding="1rem 3rem 3rem">
					<Box h="66rem" mb="4rem">
						<MarkdownText text={_(livenessCheckInfoText)} />
						<LivenessCheck
							livenessCheckData={{
								webToken: applicationLivenessCheck.data.data.web_token,
								serviceKind: applicationLivenessCheck.data.meta.service_kind,
							}}
							onFinish={(response) => {
								const isPassed =
									settings.mode === 'permissive' ||
									(settings.mode === 'strict' &&
										(response.status === 'approved' || response.manualStatus === 'approved'));

								if (isPassed) {
									setIsFinished(true);
									applicationLivenessCheck.refetch();
								}
							}}
						/>
					</Box>
				</TFCard>
			</TFPageContent>
		);
	}

	return <>{children}</>;
};

export const LivenessCheckGuard: React.FC<React.PropsWithChildren> = ({ children }) => {
	const application = useApplication();

	const originTenantSettings = useOriginTenantSettings();
	const livenessCheckSettings = originTenantSettings.settings.liveness_check_before_application;

	const isEnabled = Boolean(
		livenessCheckSettings.enabled && livenessCheckSettings.profile_kinds?.includes(application.account.entityKind),
	);

	if (!isEnabled) {
		return <>{children}</>;
	}

	return <LivenessCheckScreen settings={livenessCheckSettings}>{children}</LivenessCheckScreen>;
};
